<template>
  <div v-if="visible" class="magic-ui-swiper magic-ui-container">
    <swiper
      slidesPerView="auto"
      :style="{
        '--swiper-navigation-color': config.navigationColor,
        '--swiper-pagination-color': config.navigationColor,
        '--swiper-navigation-size': `calc(${$attrs?.style?.width || 25} / 13)`,
        width: '100%',
        height: '100%',
      }"
      :modules="modules"
      :key="`${config.autoplay}${config.circular}-${dataSourceKey}`"
      :autoplay="
        (config.autoplay && app?.mode !== 'playground')
          ? {
              delay: config.interval,
              disableOnInteraction: false,
            }
          : false
      "
      :speed="config.duration"
      :loop="app?.mode !== 'playground' && config.circular"
      :spaceBetween="config.spaceBetween"
      :freeMode="config.freeMode"
      :navigation="config.navigation"
      :pagination="
        config.indicatorDots
          ? {
              clickable: true,
              bulletClass: `swiper-pagination-bullet swiper-${config.id}-pagination-bullet`,
              bulletActiveClass: `swiper-pagination-bullet-active swiper-${config.id}-pagination-bullet-active`,
            }
          : false
      "
      @swiper="swiperRef = $event"
      @slide-change="handleSlideChange"
    >
      <swiper-slide
        v-for="(item, index) in dataSource"
        :key="index"
        :style="{
          width: `${config.itemWidth}%`,
        }"
      >
        <SwiperItemContent
          :currentItem="item"
          :index="index"
          :currentItemKey="config.currentItemKey"
          :indexKey="config.indexKey"
          :listNode="node"
        >
          <div
            class="list-item-container"
            :id="`list-item-container-${index}`"
            :style="{
              position: 'relative',
              height: '100%',
            }"
            @click="handleClickSwiperItem(index)"
          >
            <slot></slot>
            <magic-ui-component v-for="item in childrenConfig" :config="item"></magic-ui-component>
          </div>
        </SwiperItemContent>
      </swiper-slide>
    </swiper>
  </div>
</template>

<style>
.swiper-item {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
}
</style>

<script lang="ts" setup>
import { ref, watch, watchEffect, reactive, computed } from 'vue';
import { cloneDeep } from 'lodash-es';
import { MComponent } from '@tmagic/schema';
import SwiperClass, {
  Pagination,
  Autoplay,
  FreeMode,
  Navigation,
} from 'swiper';
import { Swiper, SwiperSlide } from 'vue-awesome-swiper';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';

import useApp from '@/hooks/useApp';
import useDisplay from '@/hooks/useDisplay';
import MagicUiComponent from '../../../Component.vue';

import initValue from '../initValue';

import SwiperItemContent from './SwiperItemContent.vue';

const props = defineProps<{
  config: MComponent & typeof initValue;
}>();

const app = useApp(props);
const node = app?.page?.getNode(props.config.id!);

const swiperRef = ref<SwiperClass>();
const modules = [Pagination, Autoplay, FreeMode, Navigation];

const childrenConfig = computed(() => {
  return cloneDeep(props.config.items);
});

const dataSource = computed(() => {
  return Array.isArray(props.config.dataSource) ? props.config.dataSource : [];
});

const currentClickIndex = ref<number>();
const currentClickItem = computed(() => currentClickIndex.value ? dataSource.value[currentClickIndex.value] : undefined);
const handleClickSwiperItem = (index: number) => {
  currentClickIndex.value = index;
  app?.emit('swiper:item-click', node, index);
  console.log('swiper:item-click');
};

const currentIndex = ref(0);
const currentSwiperItem = computed(() => dataSource.value[currentIndex.value]);
const handleSlideChange = ({ realIndex }: SwiperClass) => {
  currentIndex.value = realIndex;
};

watch(
  () => currentIndex.value,
  (index) => {
    console.log('swiper:change', index);
    app?.emit('swiper:change', node, index);
  }
);

watchEffect((onClear) => {
  const { id, indicatorColor, indicatorActiveColor } = props.config;
  const paginationCustomStyle = `
        .swiper-${id}-pagination-bullet {
          background-color: ${indicatorColor};
          opacity: 1;
        }
        .swiper-${id}-pagination-bullet-active {
          background-color: ${indicatorActiveColor};
        }
      `;
  const style = document.createElement('style');
  style.innerHTML = paginationCustomStyle;
  document.head.appendChild(style);
  onClear(() => {
    document.head.removeChild(style);
  });
});

const slideTo = (index: number) => {
  swiperRef.value?.slideTo(index);
};
const slideNext = () => {
  swiperRef.value?.slideNext();
};
const slidePrev = () => {
  swiperRef.value?.slidePrev();
};

const dataSourceKey = ref(0)
watch(
  () => dataSource.value,
  () => {
    dataSourceKey.value += 1
  },
  { deep: true }
)

const { visible, show, hide } = useDisplay(props);
// 暴露给编辑器的接口请注册在这里
const exportInterface = reactive({
  show,
  hide,
  data: dataSource.value,
  currentClickIndex,
  currentClickItem,
  currentIndex,
  currentSwiperItem,
  slideTo,
  slideNext,
  slidePrev,
});

defineExpose(exportInterface);
</script>

<style scoped lang="scss">
.list-item-container{
  overflow: hidden;
}
</style>
